import React, { useEffect, useMemo, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, runTransaction, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import Spinner from './Spinner';
import Notification from './Notification';
import { hasStaffFeature, isFullAdminFromStorage, isViewerFromStorage } from '../utils/staffAccess';
import './AdminDashboard.css';
import './AdminStaffUsersPage.css';
import './AdminSuppliersPage.css';

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return value instanceof Date ? value : null;
};

const formatMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '$0,00';
  return `$${x.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const emptySupplierForm = () => ({
  name: '',
  email: '',
  phone: '',
  cuit: '',
  address: '',
  notes: '',
});

const supplierDocIdFromName = (name) => {
  const normalized = normalizeText(name);
  return normalized
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
};

const quantitiesByProductId = (lines) => {
  const byId = {};
  for (const line of lines || []) {
    const productId = line?.productId;
    if (!productId) continue;
    const qty = Number(line?.quantityAdded) || 0;
    byId[productId] = (byId[productId] || 0) + qty;
  }
  return byId;
};

const canAccessSuppliers = () =>
  isFullAdminFromStorage() ||
  hasStaffFeature('localSales') ||
  hasStaffFeature('adminOrders') ||
  hasStaffFeature('adminSalesMp');

const normalizePaymentStatus = (value) => {
  const v = normalizeText(value);
  if (v === 'pagado') return 'Pagado';
  return 'Adeudado';
};

const AdminSuppliersPage = () => {
  const readOnly = isViewerFromStorage();
  const [suppliers, setSuppliers] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [form, setForm] = useState(emptySupplierForm());
  const [newSupplierForm, setNewSupplierForm] = useState(emptySupplierForm());
  const [saving, setSaving] = useState(false);
  const [editExpanded, setEditExpanded] = useState(false);
  const [createExpanded, setCreateExpanded] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false, type: 'success' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [suppliersSnap, historySnap] = await Promise.all([
          getDocs(collection(db, 'suppliers')),
          getDocs(collection(db, 'stockHistory')),
        ]);
        if (cancelled) return;
        const suppliersRows = suppliersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const historyRows = historySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSuppliers(suppliersRows);
        setStockHistory(historyRows);
      } catch (error) {
        console.error('Error al cargar proveedores:', error);
        setSuppliers([]);
        setStockHistory([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const purchaseOrdersBySupplier = useMemo(() => {
    const map = new Map();
    for (const row of stockHistory) {
      const supplierName = String(row.supplierName || '').trim();
      if (!supplierName || !Array.isArray(row.lines) || row.lines.length === 0) continue;
      const norm = normalizeText(supplierName);
      if (!map.has(norm)) map.set(norm, []);
      map.get(norm).push(row);
    }
    return map;
  }, [stockHistory]);

  const suppliersWithComputedData = useMemo(() => {
    const byId = suppliers.map((supplier) => {
      const name = String(supplier.name || '').trim();
      const norm = normalizeText(name);
      const orders = purchaseOrdersBySupplier.get(norm) || [];
      const orderCount = orders.length;
      const units = orders.reduce(
        (acc, order) => acc + (order.lines || []).reduce((lineAcc, l) => lineAcc + (Number(l.quantityAdded) || 0), 0),
        0
      );
      const amount = orders.reduce((acc, order) => {
        const orderAmount = (order.lines || []).reduce((lineAcc, l) => {
          const q = Number(l.quantityAdded);
          const unit = Number.isFinite(Number(l.costPriceAfter)) ? Number(l.costPriceAfter) : Number(l.previousCostPrice);
          if (!Number.isFinite(q) || !Number.isFinite(unit)) return lineAcc;
          return lineAcc + q * unit;
        }, 0);
        return acc + orderAmount;
      }, 0);
      const debtAmount = orders.reduce((acc, order) => {
        const status = normalizePaymentStatus(order.supplierPaymentStatus);
        if (status !== 'Adeudado') return acc;
        const orderAmount = (order.lines || []).reduce((lineAcc, l) => {
          const q = Number(l.quantityAdded);
          const unit = Number.isFinite(Number(l.costPriceAfter)) ? Number(l.costPriceAfter) : Number(l.previousCostPrice);
          if (!Number.isFinite(q) || !Number.isFinite(unit)) return lineAcc;
          return lineAcc + q * unit;
        }, 0);
        return acc + orderAmount;
      }, 0);
      const lastPurchase =
        toDate(supplier.lastPurchaseAt) ||
        orders
          .map((o) => toDate(o.createdAt))
          .filter(Boolean)
          .sort((a, b) => b.getTime() - a.getTime())[0] ||
        null;
      return {
        id: supplier.id,
        name: name || 'Sin nombre',
        norm,
        orderCount,
        units,
        amount,
        debtAmount,
        lastPurchase,
        orders,
        supplierDoc: supplier,
      };
    });

    const namesInSuppliers = new Set(byId.map((s) => s.norm));
    for (const [norm, orders] of purchaseOrdersBySupplier.entries()) {
      if (namesInSuppliers.has(norm)) continue;
      const firstName = String(orders[0]?.supplierName || '').trim();
      const units = orders.reduce(
        (acc, order) => acc + (order.lines || []).reduce((lineAcc, l) => lineAcc + (Number(l.quantityAdded) || 0), 0),
        0
      );
      const amount = orders.reduce((acc, order) => {
        const orderAmount = (order.lines || []).reduce((lineAcc, l) => {
          const q = Number(l.quantityAdded);
          const unit = Number.isFinite(Number(l.costPriceAfter)) ? Number(l.costPriceAfter) : Number(l.previousCostPrice);
          if (!Number.isFinite(q) || !Number.isFinite(unit)) return lineAcc;
          return lineAcc + q * unit;
        }, 0);
        return acc + orderAmount;
      }, 0);
      const debtAmount = orders.reduce((acc, order) => {
        const status = normalizePaymentStatus(order.supplierPaymentStatus);
        if (status !== 'Adeudado') return acc;
        const orderAmount = (order.lines || []).reduce((lineAcc, l) => {
          const q = Number(l.quantityAdded);
          const unit = Number.isFinite(Number(l.costPriceAfter)) ? Number(l.costPriceAfter) : Number(l.previousCostPrice);
          if (!Number.isFinite(q) || !Number.isFinite(unit)) return lineAcc;
          return lineAcc + q * unit;
        }, 0);
        return acc + orderAmount;
      }, 0);
      const lastPurchase =
        orders
          .map((o) => toDate(o.createdAt))
          .filter(Boolean)
          .sort((a, b) => b.getTime() - a.getTime())[0] || null;
      byId.push({
        id: `legacy-${norm}`,
        name: firstName || 'Sin nombre',
        norm,
        orderCount: orders.length,
        units,
        amount,
        debtAmount,
        lastPurchase,
        orders,
        supplierDoc: null,
      });
    }

    return byId.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }, [suppliers, purchaseOrdersBySupplier]);

  const filteredSuppliers = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return suppliersWithComputedData;
    return suppliersWithComputedData.filter((supplier) => {
      const last = supplier.lastPurchase ? supplier.lastPurchase.toLocaleDateString('es-AR') : '';
      return (
        normalizeText(supplier.name).includes(term) ||
        normalizeText(last).includes(term)
      );
    });
  }, [suppliersWithComputedData, search]);

  const totalDebtAmount = useMemo(() => {
    return stockHistory.reduce((acc, order) => {
      const status = normalizePaymentStatus(order.supplierPaymentStatus);
      if (status !== 'Adeudado') return acc;
      const lines = Array.isArray(order.lines) ? order.lines : [];
      const amount = lines.reduce((lineAcc, l) => {
        const q = Number(l.quantityAdded);
        const unit = Number.isFinite(Number(l.costPriceAfter)) ? Number(l.costPriceAfter) : Number(l.previousCostPrice);
        if (!Number.isFinite(q) || !Number.isFinite(unit)) return lineAcc;
        return lineAcc + q * unit;
      }, 0);
      return acc + amount;
    }, 0);
  }, [stockHistory]);

  const selectedSupplier =
    filteredSuppliers.find((s) => s.id === selectedSupplierId) ||
    suppliersWithComputedData.find((s) => s.id === selectedSupplierId) ||
    null;

  useEffect(() => {
    if (!selectedSupplier) {
      setForm(emptySupplierForm());
      setEditExpanded(false);
      return;
    }
    const docData = selectedSupplier.supplierDoc || {};
    setForm({
      name: selectedSupplier.name || '',
      email: docData.email || '',
      phone: docData.phone || '',
      cuit: docData.cuit || '',
      address: docData.address || '',
      notes: docData.notes || '',
    });
    setEditExpanded(false);
  }, [selectedSupplier]);

  const showMessage = (message, type = 'success') => {
    setNotification({ message, show: true, type });
  };

  const handleSaveSupplier = async () => {
    if (readOnly || !selectedSupplier || saving) return;
    const name = String(form.name || '').trim();
    if (!name) {
      showMessage('El nombre del proveedor es obligatorio.', 'error');
      return;
    }
    setSaving(true);
    try {
      const oldName = String(selectedSupplier.name || '').trim();
      const oldNorm = normalizeText(oldName);
      const nextNorm = normalizeText(name);
      const supplierId = selectedSupplier.supplierDoc?.id || supplierDocIdFromName(name);
      if (!supplierId) {
        showMessage('No se pudo generar el identificador del proveedor.', 'error');
        setSaving(false);
        return;
      }
      const batch = writeBatch(db);
      const supplierRef = doc(db, 'suppliers', supplierId);
      batch.set(
        supplierRef,
        {
          name: name.slice(0, 120),
          normalizedName: nextNorm.slice(0, 120),
          email: String(form.email || '').trim().slice(0, 180),
          phone: String(form.phone || '').trim().slice(0, 80),
          cuit: String(form.cuit || '').trim().slice(0, 40),
          address: String(form.address || '').trim().slice(0, 220),
          notes: String(form.notes || '').trim().slice(0, 600),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (oldNorm && oldNorm !== nextNorm) {
        for (const row of stockHistory) {
          const rowNorm = normalizeText(row.supplierName || '');
          if (rowNorm !== oldNorm) continue;
          const histRef = doc(db, 'stockHistory', row.id);
          batch.update(histRef, { supplierName: name.slice(0, 120), supplierNormalizedName: nextNorm.slice(0, 120) });
        }
      }

      await batch.commit();

      const [suppliersSnap, historySnap] = await Promise.all([
        getDocs(collection(db, 'suppliers')),
        getDocs(collection(db, 'stockHistory')),
      ]);
      setSuppliers(suppliersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setStockHistory(historySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSelectedSupplierId(supplierId);
      setEditExpanded(false);
      showMessage('Proveedor actualizado correctamente.', 'success');
    } catch (error) {
      console.error('Error al guardar proveedor:', error);
      showMessage('No se pudo guardar el proveedor. Revisá permisos o conexión.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSupplier = async () => {
    if (readOnly || saving) return;
    const name = String(newSupplierForm.name || '').trim();
    if (!name) {
      showMessage('Para crear proveedor, el nombre es obligatorio.', 'error');
      return;
    }
    const supplierId = supplierDocIdFromName(name);
    if (!supplierId) {
      showMessage('No se pudo generar un identificador válido para el proveedor.', 'error');
      return;
    }
    const exists = suppliers.some((s) => normalizeText(s.name || '') === normalizeText(name));
    if (exists) {
      showMessage('Ya existe un proveedor con ese nombre.', 'error');
      return;
    }
    setSaving(true);
    try {
      const batch = writeBatch(db);
      const supplierRef = doc(db, 'suppliers', supplierId);
      batch.set(
        supplierRef,
        {
          name: name.slice(0, 120),
          normalizedName: normalizeText(name).slice(0, 120),
          email: String(newSupplierForm.email || '').trim().slice(0, 180),
          phone: String(newSupplierForm.phone || '').trim().slice(0, 80),
          cuit: String(newSupplierForm.cuit || '').trim().slice(0, 40),
          address: String(newSupplierForm.address || '').trim().slice(0, 220),
          notes: String(newSupplierForm.notes || '').trim().slice(0, 600),
          purchaseCount: 0,
          totalPurchasedAmount: 0,
          totalPurchasedUnits: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await batch.commit();

      const suppliersSnap = await getDocs(collection(db, 'suppliers'));
      setSuppliers(suppliersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSelectedSupplierId(supplierId);
      setNewSupplierForm(emptySupplierForm());
      setCreateExpanded(false);
      showMessage('Proveedor creado correctamente.', 'success');
    } catch (error) {
      console.error('Error al crear proveedor:', error);
      showMessage('No se pudo crear el proveedor.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSupplier = async () => {
    if (readOnly || saving || !selectedSupplier) return;
    const supplierDocId = selectedSupplier.supplierDoc?.id;
    if (!supplierDocId) {
      showMessage('Este proveedor proviene solo de historial y no tiene ficha para eliminar.', 'error');
      return;
    }
    const supplierName = selectedSupplier.name || 'este proveedor';
    const confirmed = window.confirm(
      `¿Eliminar ${supplierName}?\n\nSe eliminará su ficha de la base de proveedores, pero no se borrarán las compras ya registradas en historial.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await deleteDoc(doc(db, 'suppliers', supplierDocId));
      const suppliersSnap = await getDocs(collection(db, 'suppliers'));
      setSuppliers(suppliersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSelectedSupplierId(null);
      setForm(emptySupplierForm());
      setEditExpanded(false);
      showMessage('Proveedor eliminado correctamente.', 'success');
    } catch (error) {
      console.error('Error al eliminar proveedor:', error);
      showMessage('No se pudo eliminar el proveedor.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSetOrderPaymentStatus = async (orderId, nextStatus) => {
    if (readOnly || saving || !orderId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'stockHistory', orderId), {
        supplierPaymentStatus: nextStatus,
      });
      setStockHistory((prev) =>
        prev.map((row) => (row.id === orderId ? { ...row, supplierPaymentStatus: nextStatus } : row))
      );
      showMessage(`Compra marcada como ${nextStatus.toLowerCase()}.`, 'success');
    } catch (error) {
      console.error('Error actualizando estado de pago:', error);
      showMessage('No se pudo actualizar el estado de pago de la compra.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUndoAndDeleteOrder = async (orderId) => {
    if (readOnly || saving || !orderId) return;
    const confirmed = window.confirm(
      '¿Deshacer y eliminar esta compra?\n\nSe restará del stock actual lo que se había sumado en esta carga y luego se eliminará el registro.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await runTransaction(db, async (tx) => {
        const histRef = doc(db, 'stockHistory', orderId);
        const histSnap = await tx.get(histRef);
        if (!histSnap.exists()) throw new Error('MISSING');
        const lines = histSnap.data().lines || [];
        const byId = quantitiesByProductId(lines);
        const productIds = Object.keys(byId);

        const productReads = await Promise.all(
          productIds.map(async (productId) => {
            const pref = doc(db, 'products', productId);
            const snap = await tx.get(pref);
            return { productId, pref, snap };
          })
        );

        for (const { productId, snap } of productReads) {
          if (!snap.exists()) throw new Error('NO_PRODUCT');
          const currentStock = Number(snap.data().stock) || 0;
          const newStock = currentStock - byId[productId];
          if (newStock < 0) throw new Error('NEGATIVE_STOCK');
        }

        for (const { productId, pref, snap } of productReads) {
          const currentStock = Number(snap.data().stock) || 0;
          const newStock = currentStock - byId[productId];
          tx.update(pref, { stock: newStock });
        }

        tx.delete(histRef);
      });

      const [suppliersSnap, historySnap] = await Promise.all([
        getDocs(collection(db, 'suppliers')),
        getDocs(collection(db, 'stockHistory')),
      ]);
      setSuppliers(suppliersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setStockHistory(historySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      showMessage('Compra deshecha y eliminada correctamente.', 'success');
    } catch (error) {
      console.error('Error deshaciendo compra:', error);
      if (error?.message === 'NEGATIVE_STOCK') {
        showMessage(
          'No se puede deshacer: el stock actual es menor al que ingresó esta compra. Ajustá inventario primero.',
          'error'
        );
      } else if (error?.message === 'NO_PRODUCT') {
        showMessage('No se puede deshacer: hay productos de esta compra que ya no existen.', 'error');
      } else {
        showMessage('No se pudo deshacer y eliminar la compra.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessSuppliers()) {
    return (
      <div className="admin-dashboard admin-suppliers-page">
        <div className="admin-suppliers-page__toolbar">
          <Link to="/admin" className="admin-staff-page__back">← Panel</Link>
        </div>
        <p className="admin-suppliers-page__empty">No tenés permisos para ver proveedores.</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard admin-suppliers-page">
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />
      <div className="admin-suppliers-page__toolbar admin-customers-page__toolbar">
        <h1>Proveedores y compras</h1>
        <span className="admin-suppliers-page__debt-badge" title="Suma de compras adeudadas">
          Deuda total: {formatMoney(totalDebtAmount)}
        </span>
        <div className="admin-suppliers-page__back-wrap">
          <Link to="/admin" className="admin-staff-page__back">← Volver al panel</Link>
        </div>
      </div>
      <section className="admin-suppliers-page__create-panel">
        <div className="admin-suppliers-page__edit-header">
          <h3>Crear proveedor</h3>
          <button
            type="button"
            className="admin-suppliers-page__toggle-edit-btn"
            onClick={() => setCreateExpanded((prev) => !prev)}
            disabled={saving}
          >
            {createExpanded ? 'Cerrar' : 'Nuevo proveedor'}
          </button>
        </div>
        {createExpanded ? (
          <>
            <div className="admin-suppliers-page__form-grid">
              <label>
                Nombre
                <input
                  value={newSupplierForm.name}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={newSupplierForm.email}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, email: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
              <label>
                Teléfono
                <input
                  value={newSupplierForm.phone}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, phone: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
              <label>
                CUIT
                <input
                  value={newSupplierForm.cuit}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, cuit: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
              <label className="admin-suppliers-page__field-span2">
                Dirección
                <input
                  value={newSupplierForm.address}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, address: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
              <label className="admin-suppliers-page__field-span2">
                Notas
                <textarea
                  rows={3}
                  value={newSupplierForm.notes}
                  onChange={(e) => setNewSupplierForm((prev) => ({ ...prev, notes: e.target.value }))}
                  disabled={readOnly || saving}
                />
              </label>
            </div>
            <div className="admin-suppliers-page__edit-actions">
              <button type="button" onClick={handleCreateSupplier} disabled={readOnly || saving}>
                {saving ? 'Creando…' : 'Crear proveedor'}
              </button>
            </div>
          </>
        ) : null}
      </section>
      {readOnly && (
        <div className="admin-suppliers-page__banner" role="status">
          Modo solo lectura: no podés guardar cambios en proveedores.
        </div>
      )}

      <div className="admin-suppliers-page__layout">
        <section className="admin-suppliers-page__list-panel">
          <div className="admin-suppliers-page__list-head">
            <h2>Base de proveedores</h2>
            <input
              type="search"
              className="admin-suppliers-page__search"
              placeholder="Buscar proveedor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className="admin-suppliers-page__spinner-wrap"><Spinner /></div>
          ) : filteredSuppliers.length === 0 ? (
            <p className="admin-suppliers-page__empty">No hay proveedores para mostrar.</p>
          ) : (
            <ul className="admin-suppliers-page__list">
              {filteredSuppliers.map((supplier) => (
                <li key={supplier.id}>
                  <button
                    type="button"
                    className={`admin-suppliers-page__list-item ${selectedSupplier?.id === supplier.id ? 'is-selected' : ''}`}
                    onClick={() => setSelectedSupplierId(supplier.id)}
                  >
                    <span className="admin-suppliers-page__list-title">{supplier.name}</span>
                    <span className="admin-suppliers-page__list-meta">
                      {supplier.orderCount} compras · {supplier.units} u. · {formatMoney(supplier.amount)}
                    </span>
                    <span className="admin-suppliers-page__list-meta admin-suppliers-page__list-debt">
                      Deuda: {formatMoney(supplier.debtAmount || 0)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-suppliers-page__detail-panel">
          <h2>Detalle de proveedor</h2>
          {!selectedSupplier ? (
            <p className="admin-suppliers-page__empty">Elegí un proveedor para ver su historial de compras.</p>
          ) : (
            <>
              <div className="admin-suppliers-page__summary">
                <p><strong>Proveedor:</strong> {selectedSupplier.name}</p>
                <p><strong>Compras registradas:</strong> {selectedSupplier.orderCount}</p>
                <p><strong>Unidades compradas:</strong> {selectedSupplier.units}</p>
                <p><strong>Total comprado:</strong> {formatMoney(selectedSupplier.amount)}</p>
                <p><strong>Total adeudado:</strong> {formatMoney(selectedSupplier.debtAmount || 0)}</p>
                <p>
                  <strong>Última compra:</strong>{' '}
                  {selectedSupplier.lastPurchase ? selectedSupplier.lastPurchase.toLocaleString('es-AR') : '—'}
                </p>
              </div>

              <div className="admin-suppliers-page__edit-card">
                <div className="admin-suppliers-page__edit-header">
                  <h3>Editar datos del proveedor</h3>
                  <button
                    type="button"
                    className="admin-suppliers-page__toggle-edit-btn"
                    onClick={() => setEditExpanded((prev) => !prev)}
                    disabled={saving}
                  >
                    {editExpanded ? 'Cerrar' : 'Editar'}
                  </button>
                </div>
                {editExpanded ? (
                  <>
                    <div className="admin-suppliers-page__form-grid">
                      <label>
                        Nombre
                        <input
                          value={form.name}
                          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                      <label>
                        Email
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                      <label>
                        Teléfono
                        <input
                          value={form.phone}
                          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                      <label>
                        CUIT
                        <input
                          value={form.cuit}
                          onChange={(e) => setForm((prev) => ({ ...prev, cuit: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                      <label className="admin-suppliers-page__field-span2">
                        Dirección
                        <input
                          value={form.address}
                          onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                      <label className="admin-suppliers-page__field-span2">
                        Notas
                        <textarea
                          rows={3}
                          value={form.notes}
                          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                          disabled={readOnly || saving}
                        />
                      </label>
                    </div>
                    <div className="admin-suppliers-page__edit-actions">
                      <button type="button" onClick={handleSaveSupplier} disabled={readOnly || saving}>
                        {saving ? 'Guardando…' : 'Guardar proveedor'}
                      </button>
                      <button
                        type="button"
                        className="admin-suppliers-page__danger-btn"
                        onClick={handleDeleteSupplier}
                        disabled={readOnly || saving || !selectedSupplier?.supplierDoc?.id}
                        title={
                          selectedSupplier?.supplierDoc?.id
                            ? 'Eliminar proveedor'
                            : 'No se puede eliminar porque no tiene ficha en suppliers'
                        }
                      >
                        Eliminar proveedor
                      </button>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="admin-suppliers-page__table-wrap">
                <table className="admin-suppliers-page__table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Nombre de carga</th>
                      <th>Productos</th>
                      <th>Unidades</th>
                      <th>Total</th>
                      <th>Estado pago</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSupplier.orders
                      .slice()
                      .sort((a, b) => {
                        const d1 = toDate(a.createdAt)?.getTime() || 0;
                        const d2 = toDate(b.createdAt)?.getTime() || 0;
                        return d2 - d1;
                      })
                      .map((order) => {
                        const lines = Array.isArray(order.lines) ? order.lines : [];
                        const paymentStatus = normalizePaymentStatus(order.supplierPaymentStatus);
                        const productNames = lines
                          .map((line) => String(line.productModel || '').trim())
                          .filter(Boolean)
                          .join(', ');
                        const units = lines.reduce((acc, l) => acc + (Number(l.quantityAdded) || 0), 0);
                        const amount = lines.reduce((acc, l) => {
                          const q = Number(l.quantityAdded);
                          const unit = Number.isFinite(Number(l.costPriceAfter))
                            ? Number(l.costPriceAfter)
                            : Number(l.previousCostPrice);
                          if (!Number.isFinite(q) || !Number.isFinite(unit)) return acc;
                          return acc + q * unit;
                        }, 0);
                        return (
                          <tr key={order.id}>
                            <td>{toDate(order.createdAt)?.toLocaleString('es-AR') || '—'}</td>
                            <td>{order.operationName || 'Sin nombre'}</td>
                            <td>{productNames || '—'}</td>
                            <td>{units}</td>
                            <td>{formatMoney(amount)}</td>
                            <td>{paymentStatus}</td>
                            <td>
                              <div className="admin-suppliers-page__table-actions">
                                <button
                                  type="button"
                                  className="admin-suppliers-page__status-btn"
                                  disabled={readOnly || saving}
                                  onClick={() =>
                                    handleSetOrderPaymentStatus(
                                      order.id,
                                      paymentStatus === 'Pagado' ? 'Adeudado' : 'Pagado'
                                    )
                                  }
                                >
                                  {paymentStatus === 'Pagado' ? 'Marcar adeudado' : 'Marcar pagado'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-suppliers-page__status-btn admin-suppliers-page__status-btn-icon admin-suppliers-page__status-btn-danger"
                                  disabled={readOnly || saving}
                                  onClick={() => handleUndoAndDeleteOrder(order.id)}
                                  aria-label="Deshacer y eliminar esta compra"
                                  title="Deshacer y eliminar"
                                >
                                  <FaTrash aria-hidden focusable="false" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminSuppliersPage;
